/**
 * Site-admin Preset detail page.
 *
 * Header: name + audience badge + last materialised + status.
 * Sections: definition (constituents + individuals + managers), action bar
 * (Materialise / Edit / Flip / Delete).
 *
 * Role logic - the API returns enough to derive every gate without an extra
 * round trip:
 *
 *   - site-admin     => full set of buttons (incl. Flip, Delete, full Edit
 *                       incl. manager list).
 *   - owner          => Edit (incl. manager list), Materialise, Delete.
 *   - manager only   => Edit (sans manager list), Materialise. No Flip, no
 *                       Delete.
 *
 * We compare the API-supplied `is_manager` flag to derive manager-only.
 * Owner status comes from `preset.owner_contact_id` vs the current user's
 * contact id. Site-admin status comes from `useMyDetails().hasAdminAccess`.
 */
import { useState } from "react";
import {
  IonAlert,
  IonButton,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
} from "@ionic/react";
import {
  arrowBackOutline,
  calendarOutline,
  checkmarkCircleOutline,
  chevronDownOutline,
  chevronForwardOutline,
  createOutline,
  listOutline,
  peopleOutline,
  personOutline,
  refreshOutline,
  shieldCheckmarkOutline,
  swapHorizontalOutline,
  trashOutline,
} from "ionicons/icons";
import { useHistory, useParams } from "react-router-dom";
import { PageError, Spinner, displayMsg } from "pymble-ui/components";
import AppHeader from "../components/AppHeader";
import ContactProfileImage from "../components/ContactProfileImage";
import AuthenticationGate from "../components/AuthenticationGate";
import { useMyDetails } from "../hooks/useMyDetails";
import {
  useDeletePreset,
  useMaterialisePreset,
  usePresetDetail,
} from "../hooks/usePresets";
import { EditPresetModal } from "../components/presets/EditPresetModal";
import { FlipAudienceModal } from "../components/presets/FlipAudienceModal";
import { PresetCompanionSection } from "../components/presets/PresetCompanionSection";
import { PresetMembershipModal } from "../components/presets/PresetMembershipModal";
import { formatMemberCount } from "../components/presets/memberCount";
import { parsePresetError } from "../components/presets/presetErrorParser";
import type { Preset, PresetAudience } from "../types/preset";

import "../components/presets/presets.css";

const StaffGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, isError, user } = useMyDetails();
  if (isLoading) return <Spinner message="Checking access..." />;
  if (isError)
    return <Spinner message="Error loading user details. Please refresh." />;
  if (!user?.active?.staff) {
    return (
      <div className="ion-padding ion-text-center">
        <h2>Access Denied</h2>
        <p>This application is available to staff only.</p>
      </div>
    );
  }
  return <>{children}</>;
};

interface MyDetailsExt {
  contact_id?: number;
}

/** Colour the materialisation status value (green on success, red on
 *  failure, muted otherwise). */
const statusClass = (status: string): string => {
  const s = status.toLowerCase();
  if (s === "success") return "preset-status preset-status--success";
  if (s === "failed" || s === "error") return "preset-status preset-status--danger";
  return "preset-status preset-status--medium";
};

const DELETE_BASE_MESSAGE =
  "This soft-deletes the preset. Members will lose access immediately. " +
  "The underlying group is kept but unhooked.";

/**
 * Delete confirmation copy, naming the companion cascade when there is one.
 *
 * Deleting a source preset soft-deletes its linked companion parent group in
 * the SAME operation - both `DeletedAt` values are stamped at the same instant
 * (gm#137 smoke, `docs/preset-companion-smoke.md`). A dialog that mentions only
 * "the preset" therefore understates what the button does, and the companion is
 * an audience other people may already be targeting. The Unlink dialog in
 * PresetCompanionSection sets the standard this matches: say it is deleted, and
 * say who loses what.
 */
const deleteConfirmMessage = (preset: Preset | null): string =>
  preset?.companion_preset_id
    ? `${DELETE_BASE_MESSAGE} Its companion parent group is DELETED in the ` +
      "same operation - it is soft-deleted, not kept, and anyone targeting it " +
      "loses that audience immediately."
    : DELETE_BASE_MESSAGE;

/** Collapsible definition card: icon badge + title/count/subtitle + chevron. */
const SectionCard: React.FC<{
  icon: string;
  title: string;
  count: number;
  subtitle: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ icon, title, count, subtitle, expanded, onToggle, children }) => (
  <div className="preset-detail-section">
    <button
      type="button"
      className="preset-detail-section-head"
      aria-expanded={expanded}
      onClick={onToggle}
    >
      <span className="preset-detail-section-badge">
        <IonIcon icon={icon} aria-hidden="true" />
      </span>
      <span className="preset-detail-section-heading">
        <span className="preset-detail-section-title">
          {title}{" "}
          <span className="preset-detail-section-count">({count})</span>
        </span>
        <span className="preset-detail-section-subtitle">{subtitle}</span>
      </span>
      <IonIcon
        icon={expanded ? chevronDownOutline : chevronForwardOutline}
        aria-hidden="true"
        className="preset-detail-section-chevron"
      />
    </button>
    {expanded && <div className="preset-detail-section-body">{children}</div>}
  </div>
);

const PresetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user, hasAdminAccess } = useMyDetails();
  const preset = usePresetDetail(id);
  const materialise = useMaterialisePreset(id);
  const deletePreset = useDeletePreset();

  const [editOpen, setEditOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [flipTarget, setFlipTarget] = useState<PresetAudience | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [openSections, setOpenSections] = useState({
    groups: true,
    individuals: true,
    managers: true,
  });
  const toggleSection = (key: "groups" | "individuals" | "managers") =>
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const data = preset.data;

  // Owner check: compare numeric contact id from /my/details (the user
  // record exposes a `contact_id` field in pymble-apis' `user/my/details/`
  // response). hasAdminAccess overrides regardless.
  const myContactId = (user as MyDetailsExt | undefined)?.contact_id;
  const isOwner =
    !!data && typeof myContactId === "number" && data.owner_contact_id === myContactId;
  const isManagerOnly =
    !!data && !isOwner && !hasAdminAccess && data.is_manager === true;

  // A companion preset's membership is DERIVED. The materialiser recomputes it
  // from the source on every run, so an authored definition, a flipped
  // audience, or a direct delete each produce a state the pair cannot hold:
  // the edit is silently reverted within the hour, or the source is left
  // pointing at a preset that no longer exists. The supported way to remove a
  // companion is Unlink, on its source - see PresetCompanionSection.
  const isCompanion = !!data?.derived_from;
  const canDelete = (isOwner || hasAdminAccess) && !isCompanion;
  const canFlip = hasAdminAccess && !isCompanion;
  const canEdit = !isCompanion;
  const canEditManagers = isOwner || hasAdminAccess;

  const handleMaterialise = async () => {
    try {
      await materialise.mutateAsync();
      displayMsg("Preset re-materialised", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch (err) {
      const parsed = parsePresetError(err);
      displayMsg(parsed?.title ?? "Failed to materialise preset", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deletePreset.mutateAsync(id);
      displayMsg("Preset deleted", {
        type: "success",
        position: "top",
        duration: 1500,
      });
      history.push("/presets");
    } catch (err) {
      const parsed = parsePresetError(err);
      displayMsg(parsed?.title ?? "Failed to delete preset", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    }
  };

  const constituentLabel = (prefixed: string): string => {
    // Prefer the API's visibility-gated group-name map (apis#804). Fall back
    // to a sliced id form when a name is missing (e.g. a group the caller
    // cannot see) so the operator can at least correlate ids.
    const labels = data?.definition.constituent_group_labels ?? {};
    if (labels[prefixed]) return labels[prefixed];
    return prefixed.startsWith("Group/")
      ? `Group ${prefixed.slice(6, 14)}...`
      : prefixed;
  };

  const individualLabel = (prefixed: string): string => {
    const labels = data?.definition.individual_contact_labels ?? {};
    return labels[prefixed] ?? "Contact";
  };

  // Blob profile images are keyed by the ContactPersonal short id; chip ids
  // arrive prefixed ("ContactPersonal/<id>"), so strip the prefix for the
  // image lookup. ContactProfileImage falls back to the generic avatar when
  // the blob is missing (404s are common).
  const individualImageId = (prefixed: string): string =>
    prefixed.includes("/") ? prefixed.split("/", 2)[1] : prefixed;

  const managerLabel = (contactId: number): string => {
    const labels = data?.manager_contact_labels ?? {};
    return labels[contactId] ?? "Manager";
  };

  const ownerLabel = (contactId: number): string => {
    // Prefer the API's visibility-gated owner name (apis#1138), keeping the id
    // in parentheses so an operator can still correlate the contact. A caller
    // who cannot resolve the owner gets no label, so fall back to the bare-id
    // form this header showed before the field existed.
    const label = data?.owner_contact_label?.trim();
    return label
      ? `Owner: ${label} (${contactId})`
      : `Owner contact: ${contactId}`;
  };

  return (
    <IonPage>
      <AppHeader />
      <IonContent fullscreen>
        <AuthenticationGate>
          <StaffGate>
            {preset.isLoading ? (
              <Spinner message="Loading preset..." />
            ) : preset.isError || !data ? (
              <PageError error="Could not load preset." />
            ) : (
              <div className="preset-detail-container">
                <IonButton
                  fill="clear"
                  color="medium"
                  onClick={() => history.push("/presets")}
                >
                  <IonIcon slot="start" icon={arrowBackOutline} />
                  Back to Presets
                </IonButton>

                <div className="preset-detail-header">
                  <div className="preset-detail-title-row">
                    <h1 className="preset-detail-title">{data.name_short}</h1>
                    <span
                      className={`preset-audience-badge ${data.audience}`}
                    >
                      {data.audience}
                    </span>
                  </div>
                  {data.name_long !== data.name_short && (
                    <span className="preset-detail-longname">
                      {data.name_long}
                    </span>
                  )}
                  <div className="preset-detail-meta">
                    <span className="preset-detail-meta-item">
                      <IonIcon icon={personOutline} aria-hidden="true" />
                      {ownerLabel(data.owner_contact_id)}
                    </span>
                    <span className="preset-detail-meta-item">
                      <IonIcon icon={peopleOutline} aria-hidden="true" />
                      {/* The API zeroes member_count whenever the member list
                          is hidden, and a preset that has never materialised
                          has no count to report either. Printing the raw 0 in
                          either case reads as "this cohort has no members" -
                          and would contradict the companion section on this
                          very page, which goes through the same helper. */}
                      Members: {formatMemberCount(data, false)}
                      {data.materialisation.deferred && " (refresh pending)"}
                    </span>
                    <span className="preset-detail-meta-item">
                      <IonIcon icon={calendarOutline} aria-hidden="true" />
                      Last materialised:{" "}
                      {data.materialisation.last_run_at
                        ? new Date(
                            data.materialisation.last_run_at
                          ).toLocaleString()
                        : "Never"}
                    </span>
                    {data.materialisation.last_run_status && (
                      <span className="preset-detail-meta-item">
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          aria-hidden="true"
                        />
                        Status:{" "}
                        <span
                          className={statusClass(
                            data.materialisation.last_run_status
                          )}
                        >
                          {data.materialisation.last_run_status}
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="preset-detail-actions">
                    <IonButton
                      fill="outline"
                      color="primary"
                      onClick={handleMaterialise}
                      disabled={materialise.isPending}
                    >
                      <IonIcon slot="start" icon={refreshOutline} />
                      {materialise.isPending ? "Refreshing..." : "Materialise"}
                    </IonButton>
                    {hasAdminAccess && (
                      // The read-only way to check a roster. Until apis#1166
                      // there was none: a hidden group (every companion parent
                      // group is one) showed "Members: hidden" and no list, so
                      // the only lever was the reveal control - a persisted
                      // privacy change for the parents in it. Deliberately
                      // NOT eye-iconed and not placed beside that control: the
                      // two must not read as variations of one action.
                      <IonButton
                        fill="outline"
                        color="primary"
                        onClick={() => setMembersOpen(true)}
                      >
                        <IonIcon slot="start" icon={listOutline} />
                        Preview members
                      </IonButton>
                    )}
                    {canEdit && (
                      <IonButton
                        fill="outline"
                        color="primary"
                        onClick={() => setEditOpen(true)}
                      >
                        <IonIcon slot="start" icon={createOutline} />
                        Edit
                      </IonButton>
                    )}
                    {canFlip && (
                      <IonButton
                        fill="outline"
                        color="danger"
                        onClick={() =>
                          setFlipTarget(
                            data.audience === "shared" ? "personal" : "shared"
                          )
                        }
                      >
                        <IonIcon slot="start" icon={swapHorizontalOutline} />
                        Flip to{" "}
                        {data.audience === "shared" ? "Personal" : "Shared"}
                      </IonButton>
                    )}
                    {canDelete && (
                      <IonButton
                        fill="outline"
                        color="danger"
                        onClick={() => setDeleteConfirm(true)}
                      >
                        <IonIcon slot="start" icon={trashOutline} />
                        Delete
                      </IonButton>
                    )}
                  </div>
                  {isManagerOnly && (
                    <p className="preset-detail-manager-note">
                      You are a manager of this preset. You can edit content
                      and re-materialise, but cannot change the manager list
                      or delete the preset.
                    </p>
                  )}
                </div>

                <PresetCompanionSection
                  preset={data}
                  canManage={hasAdminAccess}
                />

                {/* A companion's Definition is empty by contract - its roster
                    is derived. Rendering the two definition cards would show
                    empty lists whose own copy points at an Edit button that is
                    deliberately absent here. */}
                {canEdit && (
                  <>
                <SectionCard
                  icon={peopleOutline}
                  title="Constituent Groups"
                  count={data.definition.constituent_group_ids.length}
                  subtitle="Groups merged into this preset"
                  expanded={openSections.groups}
                  onToggle={() => toggleSection("groups")}
                >
                  {data.definition.constituent_group_ids.length === 0 ? (
                    <p className="preset-detail-empty">No constituent groups.</p>
                  ) : (
                    <div className="preset-detail-chip-list">
                      {data.definition.constituent_group_ids.map((id) => (
                        <span key={id} className="preset-detail-chip">
                          {constituentLabel(id)}
                        </span>
                      ))}
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  icon={personOutline}
                  title="Individual Contacts"
                  count={data.definition.individual_contact_ids.length}
                  subtitle="Specific people who also receive this preset"
                  expanded={openSections.individuals}
                  onToggle={() => toggleSection("individuals")}
                >
                  {data.definition.individual_contact_ids.length === 0 ? (
                    <p className="preset-detail-empty">No individual contacts.</p>
                  ) : (
                    <div className="preset-detail-chip-list">
                      {data.definition.individual_contact_ids.map((id) => (
                        <span
                          key={id}
                          className="preset-detail-chip preset-detail-chip--person"
                        >
                          <span
                            className="preset-chip-avatar"
                            aria-hidden="true"
                          >
                            <ContactProfileImage
                              profile={{ contactId: individualImageId(id) }}
                            />
                          </span>
                          {individualLabel(id)}
                        </span>
                      ))}
                    </div>
                  )}
                </SectionCard>
                  </>
                )}

                <SectionCard
                  icon={shieldCheckmarkOutline}
                  title="Managers"
                  count={data.manager_contact_ids.length}
                  subtitle="People who can read and manage this preset"
                  expanded={openSections.managers}
                  onToggle={() => toggleSection("managers")}
                >
                  {data.manager_contact_ids.length === 0 ? (
                    <p className="preset-detail-empty">
                      {canEdit
                        ? "No managers. Add managers via Edit (owner or site-admin)."
                        : "No managers."}
                    </p>
                  ) : (
                    <div className="preset-detail-chip-list">
                      {data.manager_contact_ids.map((cid) => (
                        <span key={cid} className="preset-detail-chip">
                          {managerLabel(cid)}
                        </span>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>
            )}
          </StaffGate>
        </AuthenticationGate>
      </IonContent>

      {data && canEdit && (
        <IonModal
          isOpen={editOpen}
          onDidDismiss={() => setEditOpen(false)}
          backdropDismiss
          className="preset-edit-modal"
        >
          <EditPresetModal
            preset={data}
            canEditManagers={canEditManagers}
            onDismiss={() => setEditOpen(false)}
            onSaved={() => setEditOpen(false)}
          />
        </IonModal>
      )}

      {/* Mounted only while open, on purpose: the modal's query returns member
          identities and writes a server-side audit line, so it must fire
          because an administrator asked for it - never as a side effect of
          landing on the page. */}
      {data && hasAdminAccess && membersOpen && (
        <IonModal
          isOpen={membersOpen}
          onDidDismiss={() => setMembersOpen(false)}
          backdropDismiss
        >
          <PresetMembershipModal
            preset={data}
            onDismiss={() => setMembersOpen(false)}
          />
        </IonModal>
      )}

      {data && flipTarget && (
        <IonModal
          isOpen={!!flipTarget}
          onDidDismiss={() => setFlipTarget(null)}
          // Confirm-style modal that runs a non-atomic clear-managers ->
          // flip sequence; don't let a stray backdrop tap dismiss it
          // mid-operation (the modal's own Cancel is gated while pending).
          backdropDismiss={false}
        >
          <FlipAudienceModal
            preset={data}
            target={flipTarget}
            onDismiss={() => setFlipTarget(null)}
            onFlipped={() => setFlipTarget(null)}
          />
        </IonModal>
      )}

      <IonAlert
        isOpen={deleteConfirm}
        onDidDismiss={() => setDeleteConfirm(false)}
        header={
          data?.companion_preset_id
            ? "Delete preset and its parent group"
            : "Delete preset"
        }
        message={deleteConfirmMessage(data ?? null)}
        buttons={[
          { text: "Cancel", role: "cancel" },
          {
            text: "Delete",
            role: "destructive",
            handler: handleDelete,
          },
        ]}
      />
    </IonPage>
  );
};

export default PresetDetail;
